import { useEffect, useState } from 'react';

import Dialog from '@/components/ui/Dialog';
import { stripePromise } from '@/lib/stripe';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { Loader2 } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────

interface StripeCardModalProps {
  open: boolean;
  onClose: () => void;
  interval?: 'monthly' | 'annual';
  planLabel: string;
  onSetupIntent: () => Promise<{ clientSecret: string }>;
  onSubscribe: (paymentMethodId: string) => Promise<{ clientSecret?: string }>;
  /** Called after the full flow (subscribe + optional 3DS) succeeds */
  onSuccess?: () => void;
}

// ─── Card Element options ─────────────────────────────────────────

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#1e293b',
      '::placeholder': { color: '#94a3b8' },
      fontFamily: 'Inter, system-ui, sans-serif',
    },
    invalid: { color: '#ef4444' },
  },
};

// ─── Inner form — uses useStripe/useElements ──────────────────────

interface CardFormProps {
  clientSecret: string;
  planLabel: string;
  onSubscribe: (paymentMethodId: string) => Promise<{ clientSecret?: string }>;
  onClose: () => void;
  onSuccess?: () => void;
}

function CardForm({ clientSecret, planLabel, onSubscribe, onClose, onSuccess }: CardFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    if (!stripe || !elements) return;
    const cardElement = elements.getElement(CardElement);
    if (!cardElement) return;

    setPending(true);
    setError(null);

    // 1. Confirmer le SetupIntent (collecte carte + 3DS)
    let paymentMethodId: string | undefined;

    const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
      payment_method: { card: cardElement },
    });

    if (stripeError) {
      // Si le SetupIntent a deja reussi (retry apres timeout), on recupere le PM
      if (stripeError.code === 'setup_intent_unexpected_state') {
        const siResult = await stripe.retrieveSetupIntent(clientSecret);
        paymentMethodId =
          typeof siResult.setupIntent?.payment_method === 'string'
            ? siResult.setupIntent.payment_method
            : siResult.setupIntent?.payment_method?.id;
      }
      if (!paymentMethodId) {
        setError(stripeError.message ?? 'Une erreur est survenue.');
        setPending(false);
        return;
      }
    } else {
      paymentMethodId =
        typeof setupIntent?.payment_method === 'string' ? setupIntent.payment_method : setupIntent?.payment_method?.id;
    }

    if (!paymentMethodId) {
      setError('Echec de la configuration du moyen de paiement.');
      setPending(false);
      return;
    }

    // 2. Creer la subscription et confirmer le paiement 3DS si necessaire
    try {
      const result = await onSubscribe(paymentMethodId);

      if (result.clientSecret) {
        const { error: confirmError } = await stripe.confirmCardPayment(result.clientSecret);
        if (confirmError) {
          setError(confirmError.message ?? "Echec de l'authentification 3D Secure.");
          setPending(false);
          return;
        }
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la souscription.');
      setPending(false);
    }
  }

  return (
    <div className='p-6 space-y-4'>
      <h2 className='text-lg font-bold text-slate-900'>Souscrire — {planLabel}</h2>
      <p className='text-sm text-slate-600'>
        Renseignez votre carte bancaire pour activer votre abonnement. Vous pouvez annuler a tout moment.
      </p>
      <div className='rounded-xl border border-slate-200 bg-white p-3'>
        <CardElement options={cardElementOptions} />
      </div>
      {error && <p className='text-sm text-red-600'>{error}</p>}
      <div className='flex justify-end gap-3 pt-2'>
        <button
          type='button'
          onClick={onClose}
          disabled={pending}
          className='cursor-pointer rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50'
        >
          Annuler
        </button>
        <button
          type='button'
          onClick={() => void handleConfirm()}
          disabled={pending || !stripe}
          className='cursor-pointer inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:opacity-50'
        >
          {pending && <Loader2 className='h-4 w-4 animate-spin' />}
          Confirmer et payer
        </button>
      </div>
    </div>
  );
}

// ─── Outer shell — manages clientSecret fetch ─────────────────────

export default function StripeCardModal({
  open,
  onClose,
  planLabel,
  onSetupIntent,
  onSubscribe,
  onSuccess,
}: StripeCardModalProps) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !stripePromise) return;

    let cancelled = false;

    onSetupIntent()
      .then(({ clientSecret: cs }) => {
        if (!cancelled) setClientSecret(cs);
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setLoadError(err instanceof Error ? err.message : 'Impossible de charger le formulaire de paiement.');
      });

    return () => {
      cancelled = true;
    };
  }, [open, onSetupIntent]);

  function handleClose() {
    setClientSecret(null);
    setLoadError(null);
    onClose();
  }

  const isStripeReady = stripePromise !== null;

  const content = !isStripeReady ? (
    <div className='p-6'>
      <p className='text-sm text-slate-500'>
        La configuration Stripe n&apos;est pas disponible. Veuillez contacter le support.
      </p>
      <div className='flex justify-end mt-4'>
        <button
          type='button'
          onClick={handleClose}
          className='cursor-pointer rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50'
        >
          Fermer
        </button>
      </div>
    </div>
  ) : loadError ? (
    <div className='p-6'>
      <p className='text-sm text-red-600'>{loadError}</p>
      <div className='flex justify-end mt-4'>
        <button
          type='button'
          onClick={handleClose}
          className='cursor-pointer rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50'
        >
          Fermer
        </button>
      </div>
    </div>
  ) : !clientSecret ? (
    <div className='flex flex-col items-center justify-center gap-3 py-10'>
      <Loader2 className='h-6 w-6 animate-spin text-indigo-600' />
      <p className='text-sm text-slate-500'>Chargement...</p>
    </div>
  ) : (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <CardForm
        clientSecret={clientSecret}
        planLabel={planLabel}
        onSubscribe={onSubscribe}
        onClose={handleClose}
        onSuccess={onSuccess}
      />
    </Elements>
  );

  return (
    <Dialog open={open} onClose={handleClose} title={`Souscrire — ${planLabel}`} className='max-w-md'>
      {content}
    </Dialog>
  );
}
